<template>
    <div
        class="p-3 mb-2 rounded-lg w-75 flex flex-col extension-card"
        :style="{
            ...styles.card,
            ...withMoreInfo
        }"
    >
        <div
            class="flex items-center"
            :class="extensionCalls.length >= minActiveCallsNumber ? 'mb-2' : 'mb-4'"
            :style="styleForStatusIconColor"
        >
            <fade-transition mode="out-in">
                <el-tooltip
                    :key="extension.representativeStatus"
                    :content="statusText"
                    placement="top"
                    :open-delay="300"
                >
                    <component
                        :is="statusIcon"
                        :key="extension.representativeStatus"
                        :class="{ 'is-calling': isCalling, 'is-talking': isTalking }"
                        class="extension-card-icon"
                    />
                </el-tooltip>
            </fade-transition>
            <span
                class="text-main-xl text-steel font-bold leading-tight mx-2 break-normal cut-header extension-card-title"
            >
                {{ getRepresentativeSummery }}
            </span>
        </div>
        <div
            class="flex flex-col flex-1"
        >
            <div class="flex items-center justify-between">
                <div>
                    <span
                        v-show="extension.representativeStatus !== Statuses.LOGOUT || (extension.representativeStatus === Statuses.LOGOUT) && showLoggedOutUsersTime"
                        class="font-bold time cut-timer"
                        :style="styles.timer"
                        :class="extensionCalls.length >= minActiveCallsNumber ? 'small-text' : 'big-text'"
                    >
                        {{ timerDisplayTime }}
                    </span>
                </div>
                <span
                    class="font-bold time"
                    :class="extensionCalls.length >= minActiveCallsNumber ? 'small-text' : 'big-text'"
                >
                    <i
                        :style="{ 'color': styles.icon.color }"
                        :class="`vc-icon-${styles.icon.type}`"
                    />
                </span>
            </div>
            <div v-if="extensionCalls.length" class="mb-2">
                <call-info
                    v-for="call in extensionCalls"
                    :key="call.ivrid"
                    :call="call"
                    :settings="settings"
                >
                    <template #threshold="{statusThreshold}">
                        <IconThreshold
                            v-if="statusThreshold.show"
                            v-bind="statusThreshold.styles"
                            class="w-6 mb-1 mx-2"
                        />
                    </template>
                </call-info>
            </div>
        </div>
        <div
            v-if="extensionCalls.length > minActiveCallsNumber"
            class="w-full"
        >
            <span
                class="divide-y h-px w-full divide-horizontal mb-1"
            />
            <div
                class="cursor-pointer text-center -mb-4"
                @click="showMoreInfo"
            >
                <i
                    class="vc-icon-down text-xl text-primary"
                    :class="{ 'arrow-transition-down': additionalBlock, 'arrow-transition-up': !additionalBlock }"
                />
            </div>
        </div>
    </div>
</template>
<script>
import get from 'lodash/get'
import { Tooltip } from 'element-ui'
import statusTypes from '@/enum/statusTypes'
import { Statuses } from '@/enum/redesignStatusTypes'
import { getInitialExtensionTime } from '@/util/timeUtils'
import cloneDeep from 'lodash/cloneDeep'
import timerMixin from '@/mixins/timerMixin'
import { getQueueSounds, setupSoundQueue, removeSoundsFromQueueByIdWithHistoryKeep } from '@/composables/useSoundsStoreData'
import {
    getAccountBreakLimitByStatusId,
    getSelectedAccountStatusTextById,
    selectedAccountData
} from '@/composables/globalData/useEntitiesData'
import { getActiveLayoutColors, getSoundFromActiveLayout } from '@/composables/globalData/useLayouts'

const MIN_ACTIVE_CALLS_NUMBER = 2

export default {
    components: {
        CallInfo: () => import('./CallInfo.vue'),
        [Tooltip.name]: Tooltip,
    },
    mixins: [
        timerMixin
    ],
    props: {
        extension: {
            type: Object,
            default: () => ({}),
        },
        settings: {
            type: Object,
            default: () => ({}),
        },
        thresholdConfig: {
            type: Object,
            default: () => ({}),
        },
    },
    data () {
        const initialTimeInSeconds = getInitialExtensionTime(this.extension, this.settings)

        return {
            timerState: {
                seconds: initialTimeInSeconds,
            },
            statusMappings: statusTypes,
            withMoreInfo: {},
            additionalBlock: false,
            minActiveCallsNumber: MIN_ACTIVE_CALLS_NUMBER,
            statusThreshold: '',
            styles: {
                icon: {
                    color: 'white',
                    type: '',
                },
                timer: {
                    color: '#445162'
                },
                card: {
                    border: '1px solid white'
                }
            },
            Statuses
        }
    },
    computed: {
        getRepresentativeSummery () {
            return get(this.extension, 'summery.representative', 'userName')
        },
        statusText () {
            let text = getSelectedAccountStatusTextById(this.extension.representativeStatus)
            if (this.isTalking) {
                text = 'status.talking'
            }
            if (this.isCalling) {
                text = 'status.calling'
            }
            return this.$t(text)
        },
        statusIcon () {
            const data = this.statusMappings[this.extension.representativeStatus] || { icon: 'IconOther' }
            if (this.extension.calls.length > 0) {
                return 'IconIncomingCall'
            }
            return data.icon
        },
        isCalling () {
            if (this.extension.calls.length === 0) {
                return false
            }
            return this.extension.calls.every(c => c.callAnswered === 0)
        },
        isTalking () {
            if (this.extension.calls.length === 0) {
                return false
            }
            return this.extension.calls.every(c => c.callAnswered !== 0)
        },
        extensionCalls () {
            const extensionCalls = this.extension.calls ? cloneDeep(this.extension.calls) : []
            if (!this.additionalBlock && extensionCalls.length) {
                return extensionCalls.slice(0, 2)
            }
            return extensionCalls
        },
        getAccountStatuses () {
            return selectedAccountData.value.AccountBreaks
        },
        styleForStatusIconColor () {
            const data = this.statusMappings[this.extension.representativeStatus] || { icon: 'IconOther' }
            const dynamicColor = this.getAccountStatuses.find(el => Number(el.StatusID) === Number(data.value))
            const color = dynamicColor && dynamicColor.ColorCode ? dynamicColor.ColorCode : data.color

            return {
                '--status-svg-color': color
            }
        },
        sounds () {
            return getQueueSounds()
        },
        showLoggedOutUsersTime () {
            return get(this.settings, 'showLoggedOutUsersTime', false)
        },
    },
    methods: {
        getTimerValue () {
            return getInitialExtensionTime(this.extension, this.settings)
        },
        showMoreInfo () {
            this.additionalBlock = !this.additionalBlock
            this.withMoreInfo = {
                position: this.additionalBlock ? 'absolute' : 'relative',
                ...(this.additionalBlock && ({
                    top: '0px',
                    left: '4px',
                    'z-index': '1',
                    'box-shadow': '0px 0px 5px rgba(38, 117, 255, 0.5)'
                })),
            }
        },
        makeStyles () {
            if (!this.statusThreshold) {
                return {
                    icon: {
                        color: 'white',
                        type: '',
                    },
                    timer: {
                        color: '#445162'
                    },
                    card: {
                        border: '1px solid white'
                    }
                }
            }

            const { loginStatusLimit, loginStatusWarning } = getActiveLayoutColors()
            const color = this.statusThreshold === 'info' ? loginStatusWarning : loginStatusLimit
            const sound = this.statusThreshold === 'info' ? getSoundFromActiveLayout('SoundLoginStatusWarning') : getSoundFromActiveLayout('SoundLoginStatusLimit')

            if (this.statusThreshold && sound) {
                const notificationData = {
                    entity_name: this.getRepresentativeSummery,
                    current_value: this.timerDisplayTime,
                    status: this.statusThreshold
                }

                const soundItem = {
                    id: this.extension.userID,
                    sound: sound,
                    played: false,
                    isPlaying: false,
                    type: this.statusThreshold,
                    timestamp: new Date().getTime(),
                    notificationHTML: this.$t('widget.extensionCardHtml', { ...notificationData })
                }
                setupSoundQueue(soundItem)
            }

            return {
                icon: {
                    color: color,
                    type: this.statusThreshold,
                },
                timer: {
                    color: color
                },
                card: {
                    border: `1px solid ${color}`
                }
            }
        }
    },
    watch: {
        'extension.representativeStatus' (newStatus, oldStatus) {
            if (newStatus !== oldStatus) {
                this.resetTimer()
            }
        },
        'extension.calls': {
            deep: true,
            handler (newVal, oldVal) {
                if (newVal !== oldVal) {
                    return
                }

                if (!this.settings.resetIdleTime || !oldVal.length) {
                    this.resetTimer()
                    return
                }

                const oldCall = oldVal.find((call) => call.answered)

                if (oldCall && this.settings.resetIdleTime) {
                    this.resetTimer()
                } else {
                    this.setTimerValue(this.getTimerValue())
                }
            },
        },
        'extension.userID' (newVal, oldVal) {
            if (newVal !== oldVal) {
                this.setTimerValue(this.getTimerValue())
            }
        },
        'timerState.seconds': {
            deep: true,
            handler (val) {
                if (this.timerState.seconds === 0) {
                    removeSoundsFromQueueByIdWithHistoryKeep(this.extension.userID)
                }

                let statusThreshold = ''
                const statusAlerts = getAccountBreakLimitByStatusId(this.extension.accountID, this.extension.representativeStatus)

                if (statusAlerts && 'warn' in statusAlerts && (val >= statusAlerts.warn)) {
                    statusThreshold = 'info'
                }

                if (statusAlerts && 'alert' in statusAlerts && (val >= statusAlerts.alert)) {
                    statusThreshold = 'alert'
                }

                if (statusThreshold !== this.statusThreshold) {
                    this.statusThreshold = statusThreshold
                }
            }
        },
        'statusThreshold' (newVal, oldVal) {
            try {
                if (newVal === oldVal) {
                    return
                }
                if (!oldVal || newVal !== oldVal) {
                    removeSoundsFromQueueByIdWithHistoryKeep(this.extension.userID)
                    this.styles = this.makeStyles()
                }
            } catch (e) {
                console.log(e)
            }
        },
    },
    mounted () {
        this.startTimer()
    },
    beforeUnmount () {
        removeSoundsFromQueueByIdWithHistoryKeep(this.extension.userID)
    },
}
</script>
<style lang="scss" scoped>
@mixin cutText($maxWidth) {
    text-overflow: ellipsis;
    overflow: hidden;
    width: $maxWidth;
    white-space: nowrap;
}

.extension-card {
    min-height: 190px;
    transition: all .2s;
    position: relative;
    background: var(--mainBgColor-color);
}

.extension-card-title {
    color: var(--mainTextColor-color);
}

.extension-card-icon {
    max-width: 25px;
    min-width: 25px;
}

.cut-header {
    @include cutText(200px);
}

.cut-timer {
    @include cutText(185px);
}

.time {
    color: var(--dark-gray);
}

.small-text {
    font-size: 24px;
    line-height: 29px;
}

.big-text {
    font-size: 40px;
    line-height: 49px;
}

.arrow-transition-down {
    display: inline-block;
    transition: transform 0.5s ease-in-out;
    transform: rotate(180deg);
}

.arrow-transition-up {
    display: inline-block;
    transition: transform 0.5s ease-in-out;
    transform: rotate(0);
}

.divide-horizontal {
    display: block;
    width: 298px;
    background: var(--gray-350);
}

[dir="ltr"] .divide-horizontal {
    margin-left: -12px;
}

[dir="rtl"] .divide-horizontal {
    margin-right: -12px;
}
</style>
<style lang="scss">
@keyframes fade {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}

.extension-card-icon {
    width: var(--dynamic-font-size);
}

.extension-card-icon.is-calling {
    path:first-child {
        animation: fade 1s;
    }

    path:last-child {
        opacity: 0;
        animation: fade 1s infinite;
        animation-direction: alternate;
        animation-delay: 1s;
    }
}
</style>
